import { useCallback, useEffect, useState } from "react";
import { UNPAID_ORG_POLL_MAX_MS, UNPAID_ORG_POLL_MS } from "../../configs/unpaidInvoices.config";
import type { UnpaidAccessState } from "../../types/unpaidInvoices.types";

type ConsoleEntryPhase = "idle" | "entering" | "timedOut";

type UseEnterConsoleAfterPaymentParams = {
  entry: ConsoleEntryState
  accessState: UnpaidAccessState
  closePayment: () => void
  refreshOrganization: () => void
};

/**
 * Owns the entry phase and the polling it implies. Called before the access hook, which needs
 * `refetchInterval` to poll while the access state, read after it, ends the phase.
 */
export const useConsoleEntryState = () => {
  const [phase, setPhase] = useState<ConsoleEntryPhase>("idle");

  useEffect(() => {
    if (phase !== "entering") return undefined;
    const timer = setTimeout(() => setPhase("timedOut"), UNPAID_ORG_POLL_MAX_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  const startEntering = useCallback(() => setPhase("entering"), []);
  const stopEntering = useCallback(() => setPhase("idle"), []);

  const isEntering = phase === "entering";
  const refetchInterval: number | false = isEntering ? UNPAID_ORG_POLL_MS : false;

  return {
    isEntering,
    hasTimedOut: phase === "timedOut",
    refetchInterval,
    startEntering,
    stopEntering,
  };
};

type ConsoleEntryState = ReturnType<typeof useConsoleEntryState>;

/**
 * The card confirmation is not the unblock: the webhook lifts it a moment later. The user asks to enter,
 * the organization is read until the access is back, and only then is the held view released.
 */
export const useEnterConsoleAfterPayment = ({
  entry, accessState, closePayment, refreshOrganization,
}: UseEnterConsoleAfterPaymentParams) => {
  const { isEntering, stopEntering } = entry;

  // Guarded by the click: an organization turning pending while a payment outcome shows must not release it.
  useEffect(() => {
    if (!isEntering || accessState === "blocked") return;
    stopEntering();
    closePayment();
  }, [isEntering, accessState, stopEntering, closePayment]);

  const enterConsole = () => {
    entry.startEntering();
    refreshOrganization();
  };

  return { enterConsole };
};
